import { Suspense } from 'react';

import { lazyWithRetry as lazy } from 'src/utils/lazy-with-retry';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

// OVERVIEW
const HomePage = lazy(() => import('src/pages/dashboard/home'));

// CRM
const CRMListPage = lazy(() => import('src/pages/dashboard/crm/crm/list'));
const CRMCreatePage = lazy(() => import('src/pages/dashboard/crm/crm/new'));
const CRMEditPage = lazy(() => import('src/pages/dashboard/crm/crm/edit'));
const CRMAccountPage = lazy(() => import('src/pages/dashboard/crm/crm/account'));
const ActivityListPage = lazy(() => import('src/pages/dashboard/crm/activities/list'));

// CRM Settings
const PropertyListPage = lazy(() => import('src/pages/dashboard/crm/settings/list'));
const PropertyCreatePage = lazy(() => import('src/pages/dashboard/crm/settings/new'));
const PropertyEditPage = lazy(() => import('src/pages/dashboard/crm/settings/edit'));

// CRM Pipelines
const PipelinesListPage = lazy(() => import('src/pages/dashboard/crm/pipelines/list'));
const PipelinesCreatePage = lazy(() => import('src/pages/dashboard/crm/pipelines/new'));
const PipelinesEditPage = lazy(() => import('src/pages/dashboard/crm/pipelines/edit'));

// CRM Data Import
const DataImportListPage = lazy(() => import('src/pages/dashboard/crm/data_import/list'));
const DataImportCreatePage = lazy(() => import('src/pages/dashboard/crm/data_import/new'));
const DataImportEditPage = lazy(() => import('src/pages/dashboard/crm/data_import/edit'));

// CRM Layouts
const LayoutsListPage = lazy(() => import('src/pages/dashboard/crm/layouts/list'));

// REACTIVATE PAGE
const ReactivatePage = lazy(() => import('src/pages/dashboard/reactivate'));

// USERS
const UsersListPage = lazy(() => import('src/pages/dashboard/users/users/list'));
const UsersCreatePage = lazy(() => import('src/pages/dashboard/users/users/new'));
const UsersEditPage = lazy(() => import('src/pages/dashboard/users/users/edit'));

// USERS TEAMS
const TeamsListPage = lazy(() => import('src/pages/dashboard/users/teams/list'));
const TeamsCreatePage = lazy(() => import('src/pages/dashboard/users/teams/new'));
const TeamsEditPage = lazy(() => import('src/pages/dashboard/users/teams/edit'));

// USERS Permissions
const PermissionsListPage = lazy(() => import('src/pages/dashboard/users/permissions/list'));
const PermissionsCreatePage = lazy(() => import('src/pages/dashboard/users/permissions/new'));
const PermissionsEditPage = lazy(() => import('src/pages/dashboard/users/permissions/edit'));

// WEBSITES
const WebsitesListPage = lazy(() => import('src/pages/dashboard/websites/list'));
const WebsitesCreatePage = lazy(() => import('src/pages/dashboard/websites/new'));
const WebsitesEditPage = lazy(() => import('src/pages/dashboard/websites/edit'));

// LANDING PAGES
const LandingPagesListPage = lazy(() => import('src/pages/dashboard/landing-pages/list'));
const LandingPagesCreatePage = lazy(() => import('src/pages/dashboard/landing-pages/new'));
const LandingPagesEditPage = lazy(() => import('src/pages/dashboard/landing-pages/edit'));

// BLOGS
const BlogsListPage = lazy(() => import('src/pages/dashboard/blogs/list'));
const BlogsViewPage = lazy(() => import('src/pages/dashboard/blogs/view'));
const BlogsCreatePage = lazy(() => import('src/pages/dashboard/blogs/new'));
const BlogsEditPage = lazy(() => import('src/pages/dashboard/blogs/edit'));

// REVIEWS
const ReviewsListPage = lazy(() => import('src/pages/dashboard/review/list'));
const ReviewsCreatePage = lazy(() => import('src/pages/dashboard/review/new'));
const ReviewsEditPage = lazy(() => import('src/pages/dashboard/review/edit'));

// GALLERY
const GalleryListPage = lazy(() => import('src/pages/dashboard/gallery/list'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <HomePage />, index: true },
      {
        path: 'crm',
        children: [
          {
            path: ':crmKey',
            children: [
              { element: <CRMListPage />, index: true },
              { path: 'new', element: <CRMCreatePage /> },
              { path: ':id/edit', element: <CRMEditPage /> },
              { path: ':id/account', element: <CRMAccountPage /> },
            ],
          },
          {
            path: 'activities',
            children: [
              { element: <ActivityListPage />, index: true },
            ],
          },
          {
            path: 'settings',
            children: [
              { element: <PropertyListPage />, index: true },
              { path: 'new', element: <PropertyCreatePage /> },
              { path: ':id/edit', element: <PropertyEditPage /> }
            ],
          },
          {
            path: 'pipelines',
            children: [
              { element: <PipelinesListPage />, index: true },
              { path: 'new', element: <PipelinesCreatePage /> },
              { path: ':id/edit', element: <PipelinesEditPage /> },
            ],
          },
          {
            path: 'data_import',
            children: [
              { element: <DataImportListPage />, index: true },
              { path: 'new', element: <DataImportCreatePage /> },
              { path: ':id/edit', element: <DataImportEditPage /> },
            ],
          },
          {
            path: 'layouts',
            children: [
              { path: ':crmKey', element: <LayoutsListPage /> },
            ],
          },
        ],
      },
      {
        path: 'websites',
        children: [
          { element: <WebsitesListPage />, index: true },
          { path: 'new', element: <WebsitesCreatePage /> },
          { path: ':id/edit', element: <WebsitesEditPage /> },
        ],
      },
      {
        path: 'landing-pages',
        children: [
          { element: <LandingPagesListPage />, index: true },
          { path: 'new', element: <LandingPagesCreatePage /> },
          { path: ':id/edit', element: <LandingPagesEditPage /> },
        ],
      },
      {
        path: 'users',
        children: [
          { element: <UsersListPage />, index: true },
          { path: 'new', element: <UsersCreatePage /> },
          { path: ':id/edit', element: <UsersEditPage /> },
          {
            path: 'teams',
            children: [
              { element: <TeamsListPage />, index: true },
              { path: 'new', element: <TeamsCreatePage /> },
              { path: ':id/edit', element: <TeamsEditPage /> },
            ],
          },
          {
            path: 'permissions',
            children: [
              { element: <PermissionsListPage />, index: true },
              { path: 'new', element: <PermissionsCreatePage /> },
              { path: ':id/edit', element: <PermissionsEditPage /> },
            ],
          },
        ],
      },
      {
        path: 'blogs',
        children: [
          { element: <BlogsListPage />, index: true },
          { path: 'new', element: <BlogsCreatePage /> },
          { path: ':id/edit', element: <BlogsEditPage /> },
          { path: ':id/view', element: <BlogsViewPage /> },
        ],
      },
      {
        path: 'reviews',
        children: [
          { element: <ReviewsListPage />, index: true },
          { path: 'new', element: <ReviewsCreatePage /> },
          { path: ':id/edit', element: <ReviewsEditPage /> },
        ],
      },
      {
        path: 'gallery',
        children: [
          { element: <GalleryListPage />, index: true },
        ],
      },
    ],
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      { path: 'reactivate', element: <ReactivatePage /> },
    ],
  },
];
